import { api } from "@/lib/api";
import { API_URL } from "@/lib/env";

/** サムネイルの生成状態。PDF が 1 件も無ければ null。 */
export type ThumbnailStatus = "pending" | "ready" | "failed" | "skipped";

/** 図番の読み取り（OCR）の状態。 */
export type OcrStatus =
  | "pending"
  | "ready"
  /** 読めたが 2 位の候補と僅差で、図番として採用していない。 */
  | "uncertain"
  | "failed"
  | "skipped";

/**
 * 読み取りの確からしさ。**2 位の候補とのスコア差を 3 段階に畳んだもの**。
 *
 * - `high` … ラベル（図番 / DWG.No）の隣で読めている
 * - `medium` … 採用はしたが 2 位が近い。目で見ておきたい
 * - `low` … 決められなかった。図番には入れていない
 */
export type OcrConfidence = "high" | "medium" | "low";

/** 突き合わせの結果（図面 1 枚につき 1 件）。 */
export type DrawingMatchOutcome =
  | "linked"
  /** 図番に一致する明細が 1 行も無い。**突き合わせでは明細を作らない。** */
  | "no_item"
  | "ambiguous"
  | "no_drawing_number";

/**
 * サムネイルの `<img src>` を作る。
 *
 * API は**パスだけ**を返す。絶対 URL にすると配信先ごとに値が変わり、サーバー側が
 * 公開 URL を知っている必要が出る。画面は `VITE_API_URL` を知っているので、
 * ここで繋ぐ。
 */
export function thumbnailSrc(path: string | null): string | null {
  return path == null ? null : `${API_URL}${path}`;
}

/**
 * 図面（API の DrawingResponse に対応）。**明細に添えられた図**で、PDF の実体は
 * 版（[[DrawingRevision]]）が持つ。
 *
 * 何を作るか（図番・品名・材質）は明細の側にある。``drawing_number`` はこの
 * 図面から読み取った図番で、明細の図番と突き合わせる相手。
 */
export interface Drawing {
  drawing_id: number;
  /** この図面から読み取った図番。まだ読み取っていなければ null。 */
  drawing_number: string | null;
  client_id: number | null;
  quotation_id: number | null;
  project_id: number | null;
  /** 添えられた見積明細。null ならまだどの明細にも結び付いていない。 */
  quotation_item_id: number | null;
  project_item_id: number | null;
  note: string | null;
}

/**
 * 見積明細に図面を 1 枚足す。**PDF を上げる直前に呼ぶ**。
 *
 * 明細を足しただけでは図面を作らないので（中身の無い図面行が増えるだけ）、
 * 最初の PDF を上げるときにここで作る。既に図面がある明細で呼ぶと、もう 1 枚
 * 足す（組図 + 部品図のように 1 行に複数枚あることがあるため）。
 */
export function createQuotationItemDrawing(
  quotationId: number,
  itemId: number,
): Promise<Drawing> {
  return api.post<Drawing>(`/quotations/${quotationId}/items/${itemId}/drawings`, {});
}

/** 案件明細に図面を 1 枚足す（見積明細と同じ扱い）。 */
export function createProjectItemDrawing(
  projectId: number,
  itemId: number,
): Promise<Drawing> {
  return api.post<Drawing>(`/projects/${projectId}/items/${itemId}/drawings`, {});
}

/** 図面自身の項目の更新で送れる項目。図番・品名・材質は明細が持つ。 */
export type DrawingInput = Partial<Pick<Drawing, "note">>;

/** 図面自身の項目（備考）を更新する。 */
export function updateDrawing(
  drawingId: number,
  input: DrawingInput,
): Promise<Drawing> {
  return api.patch<Drawing>(`/drawings/${drawingId}`, input);
}

/** 図面 PDF の Content-Type。署名と PUT で同じ値を使う必要がある。 */
export const PDF_CONTENT_TYPE = "application/pdf";

/** 図面の版（API の DrawingRevisionResponse に対応）。 */
export interface DrawingRevision {
  drawing_revision_id: number;
  drawing_id: number;
  revision: string;
  original_filename: string | null;
  received_at: string | null;
  note: string | null;
  /**
   * QR を押印した版か。
   *
   * **版表記では分からない**（客先の版表記と同じ採番を使う）ので、履歴では
   * これを見てバッジを出す。現場へ出す紙はこちら。
   */
  has_qr: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface DrawingRevisionListResponse {
  items: DrawingRevision[];
}

export function listDrawingRevisions(
  drawingId: number,
): Promise<DrawingRevisionListResponse> {
  return api.get<DrawingRevisionListResponse>(`/drawings/${drawingId}/revisions`);
}

interface UploadUrlResponse {
  upload_url: string;
  s3_key: string;
  expires_in: number;
}

export function deleteDrawingRevision(
  drawingId: number,
  revisionId: number,
): Promise<void> {
  return api.delete<void>(`/drawings/${drawingId}/revisions/${revisionId}`);
}

/**
 * 図面 PDF をアップロードして版として登録する。
 *
 * 1. API から presigned URL を受け取る
 * 2. **ブラウザから S3 へ直接 PUT する**（API はファイル本体を中継しない）
 * 3. アップロードしたキーを添えて版を登録する
 *
 * 2 の PUT は S3 が相手なので、共通の api クライアント（Cookie 付き・
 * API_URL 前提）ではなく素の fetch を使う。`Content-Type` は署名に含まれるため、
 * 発行時と同じ値を必ず送る（ブラウザが file.type を空で返すことがあるので
 * 定数を使う）。
 */
export async function uploadDrawingRevision(
  drawingId: number,
  file: File,
  options: {
    /**
     * 版。**省くとサーバー側で採番する**（登録済みの数字の版の最大 + 1）。
     * 差し替えのたびに採番の規則を思い出させないため、画面からは渡さない。
     */
    revision?: string;
    /**
     * 図番を読み取る（OCR）か。**どの明細の図面かが分かっていて、図番を明細から
     * 入れている場合は false** にする。読んでも採用されない（図面の図番が空の
     * ときしか埋まらない）のに、ワーカーの番を 1 枚ぶん使うため。サムネイルは
     * 別の処理なので false でも作られる。
     */
    readNumber?: boolean;
  } = {},
): Promise<DrawingRevision> {
  const issued = await api.post<UploadUrlResponse>(
    `/drawings/${drawingId}/revisions/upload-url`,
    { filename: file.name, content_type: PDF_CONTENT_TYPE },
  );

  const res = await fetch(issued.upload_url, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": PDF_CONTENT_TYPE },
  });
  if (!res.ok) {
    throw new Error(
      `PDF のアップロードに失敗しました（${res.status}）。もう一度お試しください。`,
    );
  }

  return api.post<DrawingRevision>(`/drawings/${drawingId}/revisions`, {
    // 省略するとサーバーが採番する。undefined は JSON に出ない。
    revision: options.revision,
    s3_key: issued.s3_key,
    original_filename: file.name,
    read_number: options.readNumber,
  });
}

/**
 * 版の PDF を閲覧するための URL を取得する。
 *
 * presigned URL には期限がある（既定 15 分）ので、一覧と一緒には受け取らず
 * 開く直前に取りに来る。
 */
export async function getDrawingRevisionUrl(
  drawingId: number,
  revisionId: number,
): Promise<string> {
  const { download_url } = await api.get<{ download_url: string }>(
    `/drawings/${drawingId}/revisions/${revisionId}/download-url`,
  );
  return download_url;
}

/** 版の PDF を新しいタブで開く。 */
export async function openDrawingRevision(
  drawingId: number,
  revisionId: number,
): Promise<void> {
  const url = await getDrawingRevisionUrl(drawingId, revisionId);
  window.open(url, "_blank", "noopener,noreferrer");
}
